/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 添加到文件夹组件 V2（支持新的 Redux Slice 架构）
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-11-28 10:06:04
 */

/**
 * 本文件是添加到文件夹组件的 V2 版本，与同目录下的 index.jsx（旧版本）的主要区别：
 *
 * 1. **数据源支持不同**
 *    - V2 版本：支持 'content'（photosByContentSlice）和 'precise'（photosByPreciseSlice）数据源
 *    - 旧版本：支持 'legacy'（传统 photo state）、'location'（photosByLocationSlice）、'precise'（photosByPreciseSlice）
 *
 * 2. **数据获取方式不同**
 *    - V2 版本：使用 getSelectionConfig() + useAppSelector，直接从 Redux slice 获取数据
 *    - 旧版本：使用 usePhotosData() hook，通过统一的 hook 封装不同数据源
 *
 * 3. **数据类型处理不同**
 *    - V2 版本：使用 Set<number> 存储选中照片，通过 normalizeToSet() 标准化
 *    - 旧版本：使用 number[] 数组存储选中照片
 *
 * 4. **技术栈不同**
 *    - V2 版本：TypeScript，完整的类型定义
 *    - 旧版本：JavaScript
 *
 * 5. **使用场景不同**
 *    - V2 版本：专为 photov2 页面设计，使用新的 photosByContentSlice 状态管理
 *    - 旧版本：用于传统的 photo 页面和其他使用旧状态管理的页面
 *
 * 6. **FoldersPicker props 不同**
 *    - V2 版本：传递 resource={{ photos: [] }} prop（FoldersPicker 的必需 prop）
 *    - 旧版本：不传递 resource prop（可能在某些场景下会有问题）
 */

import React, { useState, useMemo } from 'react'
import { Button } from 'antd'
import { useAppSelector } from '../../../app/hooks'
import CONFIG from '../../../constants/config'
import FoldersPicker from './FoldersPicker'
import SelectPhotoTip from '../SelectPhotoTip'
import {
  PhotoSelectionSource,
  useActiveSelection
} from '../../ai-photo-king/utils/selectionHelpers'
import { PlusOutlined } from '@ant-design/icons'

interface AddToFolderV2Props {
  onSubmit: (params: {
    folderIDs: string[]
    submitCallback: () => void
  }) => void
  checkedNum?: number
  isRequireCheckPhoto?: boolean
  sensorReport?: () => void
  place?: string
  source?: number
  style?: React.CSSProperties
  isPermanent?: boolean
  dataSource?: PhotoSelectionSource
}

/**
 * 添加到文件夹组件 V2
 *
 * 专为 photov2 页面设计，支持新的 Redux Slice 架构
 * 支持的数据源：'content'（photosByContentSlice）、'precise'（photosByPreciseSlice）
 */
const AddToFolderV2: React.FC<AddToFolderV2Props> = ({
  onSubmit,
  checkedNum = 0,
  isRequireCheckPhoto = false,
  sensorReport = () => {
    /* noop */
  },
  place = 'allPhoto',
  source = 4,
  style = {},
  isPermanent = false,
  dataSource = 'content'
}) => {
  const [dialogVisible, setDialogVisible] = useState(false)

  const { activeCheckAll: checkAll, activeCheckedPhotos: checkedPhotos } =
    useActiveSelection(dataSource)

  const myRole = useAppSelector((state) => state.group.myRole)
  const viewPerm = useAppSelector((state) => state.group.viewPerm)

  // 判断按钮是否显示
  const isShow = useMemo(() => {
    if (
      myRole === CONFIG.memberRoleTypes.ADMIN ||
      myRole === CONFIG.memberRoleTypes.MASTER_ADMIN ||
      viewPerm === 0
    ) {
      return !isRequireCheckPhoto || checkedPhotos.size > 0 || checkAll
    }

    return false
  }, [checkedPhotos.size, checkAll, myRole, viewPerm, isRequireCheckPhoto])

  // 按钮组件
  const Btn = () => {
    // 根据来源设置不同的按钮样式
    const btnProps =
      source === 6
        ? {
            type: 'primary' as const,
            ghost: true
          }
        : {
            type: 'default' as const,
            ghost: !isShow
          }
    const btn = (
      <Button
        disabled={!isShow}
        {...btnProps}
        style={{
          marginRight: 10,
          ...style
        }}
        onClick={() => {
          sensorReport()
          setDialogVisible(true)
        }}
      >
        {source === 6 ? (
          '复制到'
        ) : (
          <>
            <PlusOutlined />
            文件夹
          </>
        )}
      </Button>
    )

    // 如果是永久显示，则使用SelectPhotoTip包装
    if (isPermanent) {
      return isShow ? btn : <SelectPhotoTip>{btn}</SelectPhotoTip>
    }

    return isShow ? btn : null
  }

  if (isShow || isPermanent) {
    return (
      <>
        <Btn />

        <FoldersPicker
          title={source === 6 ? '复制到文件夹' : '添加至文件夹'}
          visible={dialogVisible}
          checkedNum={checkedNum}
          onClose={() => setDialogVisible(false)}
          onSubmit={onSubmit}
          place={place}
          source={source}
          dataSource={dataSource}
          resource={{ photos: [] }}
        />
      </>
    )
  }

  return null
}

export default AddToFolderV2
